import { COMPANY, amountToWordsInr, formatCompanyLocality, type ReceiptDto } from '@gty/common';

import { cn } from '../ui/cn';
import { formatCurrency, formatShortDate } from '../ui/format';
import { PAYMENT_STATUS_LABEL, PAYMENT_STATUS_MEANING, countsTowardPaid } from './payment-status';

/**
 * The A4 payment receipt.
 *
 * FIXED 794px WIDE and one sheet tall, the same convention the invoice, voucher
 * and credit note use — 794 x 1123 is A4 at 96dpi, and `PdfService` sets exactly
 * that page box. Nothing here is responsive on purpose: a document that reflows
 * is a document that prints differently from its preview.
 *
 * A RECEIPT IS IMMUTABLE — but, as of the 2026-10-23 revision-visibility
 * change, only PART of it is frozen. The amount received, date/time, mode,
 * reference, receipt number and `paidToDate` (what THIS payment and
 * everything before it actually settled) never change: they are what was
 * acknowledged, and an issued receipt does not get to revise that. `total`
 * and `remaining` are the opposite — the server's `bookingTotal` is the
 * booking's CURRENT agreed value, so this document deliberately prints the
 * booking's live standing against a fixed record of what this payment
 * settled, not two different snapshots of the same old moment. Nothing is
 * recomputed HERE either way — `remaining` is `bookingTotal - paidToDate`,
 * both server-supplied, never re-derived from a second arithmetic path in
 * this component.
 *
 * NEVER PRINT "revised"/"previous"/"earlier"/"was", AND NEVER THE OLD TOTAL.
 * This document used to carry an "as at this receipt" framing precisely so a
 * reader would not mistake a frozen figure for a live one; now that the total
 * is live, that framing is gone on purpose — see the "Account position" block
 * below, which prints the three current labels with no explanatory sentence
 * to word carelessly.
 *
 * LONG-TEXT DISCIPLINE. A customer name is free text and can be far longer than
 * the artwork imagines. Every field that carries user-entered text wraps with
 * `overflow-wrap: anywhere` inside a fixed-height box, so a 200-character name
 * consumes its own card rather than pushing the balance band onto a second,
 * mostly blank page. Clipping one field is a smaller failure than paginating the
 * document.
 */

const FLUID = '[overflow-wrap:anywhere]';

function MetaRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-stretch border-b border-neutral-200 last:border-b-0">
      <div className="flex w-[26px] flex-none items-center justify-center bg-brand">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2">
          <rect x="3" y="4" width="18" height="17" rx="2" />
          <path d="M8 2v4M16 2v4M3 10h18" />
        </svg>
      </div>
      <div className="flex-1 px-[9px] py-[6px] text-[9.5px] font-semibold leading-none">{label}</div>
      <div
        className={cn(
          'w-[132px] flex-none px-[9px] py-[6px] text-[9.5px] font-bold leading-[1.25]',
          FLUID,
          accent === true ? 'text-brand' : 'text-ink',
        )}
      >
        {value}
      </div>
    </div>
  );
}

/** Red section tab — the artwork's rounded label sitting on a card's top edge. */
function SectionTab({ icon, label }: { icon: JSX.Element; label: string }): JSX.Element {
  return (
    <div className="absolute -top-[13px] left-3 z-10 inline-flex items-center gap-[7px] rounded-[7px] bg-brand px-[11px] py-[6px] text-white">
      {icon}
      <span className="text-[11px] font-bold uppercase leading-none tracking-[.6px]">{label}</span>
    </div>
  );
}

/** "Label : value" — the dotted-rule field the other documents use. */
function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-[112px] flex-none text-[9.5px] leading-[1.5]">{label}</span>
      <span className="flex-none text-[9.5px] leading-[1.5]">:</span>
      <span
        className={cn(
          'min-w-0 flex-1 border-b border-dotted border-neutral-400 pb-[2px] text-[9.5px] font-semibold leading-[1.5]',
          FLUID,
        )}
      >
        {value}
      </span>
    </div>
  );
}

const PERSON_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" />
  </svg>
);

const DOC_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </svg>
);

export function ReceiptDocument({ receipt }: { receipt: ReceiptDto }): JSX.Element {
  /**
   * "2 of 3" only once the booking is settled.
   *
   * While money is still owed nobody has committed to how many instalments will
   * follow, and the server sends `paymentCountTotal: null` rather than a
   * denominator that may turn out wrong. Printing a made-up one on a financial
   * document is worse than printing none — and unlike the screen, paper cannot
   * be corrected later.
   */
  const sequence =
    receipt.paymentCountTotal === null
      ? `Payment ${receipt.paymentNumber}`
      : `Payment ${receipt.paymentNumber} of ${receipt.paymentCountTotal}`;

  const settled = countsTowardPaid(receipt.status);
  const caveat = PAYMENT_STATUS_MEANING[receipt.status];

  /**
   * "Passengers (N): …" — names only (no age, ID or contact details), lead
   * guest first (the order `ReceiptDto.passengerNames` already arrives in).
   * Capped at 12 names then "+ N more" rather than listing everyone, so a
   * 20-passenger group booking cannot push this fixed-height sheet onto a
   * second page — the same "clip, never paginate" rule as the customer-name
   * block above, just resolved before render instead of with a CSS clip.
   */
  const PASSENGER_NAME_CAP = 12;
  const passengerSummary =
    receipt.passengerNames.length <= PASSENGER_NAME_CAP
      ? receipt.passengerNames.join(', ')
      : `${receipt.passengerNames.slice(0, PASSENGER_NAME_CAP).join(', ')} +${receipt.passengerNames.length - PASSENGER_NAME_CAP} more`;

  return (
    <article
      data-print-sheet
      className="relative flex h-[1123px] w-[794px] flex-none flex-col overflow-hidden bg-white text-ink shadow-[0_10px_34px_rgba(0,0,0,.14)]"
    >
      {/*
        THE VOID STAMP — item 3. Overlaid, not a replacement for anything
        above it: every figure on the receipt stays exactly as it was issued
        (immutable, same as always — see the class-level note), this only
        marks that the payment it acknowledges no longer counts. Visible on
        screen, in the PDF, and in a share link, because all three render
        this same component (`PdfService` drives Puppeteer at this exact
        route, and `ShareLinksService.create` renders through `PdfService`
        once at share-creation time) — one change covers all three surfaces.
      */}
      {receipt.status === 'VOIDED' && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
        >
          <div className="rotate-[-18deg] rounded-[10px] border-[6px] border-danger-ink/70 px-8 py-3 text-[56px] font-heading uppercase tracking-[6px] text-danger-ink/70">
            Void
          </div>
        </div>
      )}

      {/* ── letterhead ─────────────────────────────────────────────────── */}
      <header className="relative px-7 pt-5">
        <div className="flex items-center gap-4">
          <img src="/brand/guruji-mark.png" alt="" className="h-auto w-[74px] flex-none" />
          <div className="pt-[2px]">
            <div className="font-heading text-[34px] leading-[.92] tracking-[-1px] text-brand">
              GURUJI
            </div>
            <div className="mt-[6px] text-[19px] font-bold leading-none tracking-[1.2px]">
              TRAVEL YATRA
            </div>
            <div className="mt-[7px] text-[11.5px] leading-none">
              {COMPANY.tagline}{' '}
              <span className="font-semibold text-brand">{COMPANY.taglineAccent}</span>
            </div>
          </div>

          <div className="ml-auto w-[330px] flex-none">
            {/*
              The document's own title. `h1` rather than a styled div so the
              print page has one real heading — the PDF is a document, and a
              screen reader opening the same route should hear what it is.
            */}
            <h1 className="text-right font-heading text-[30px] leading-none tracking-[-.6px] text-brand">
              PAYMENT RECEIPT
            </h1>
            <div className="mt-[7px] text-right text-[8.6px] leading-[1.45] text-neutral-700">
              Acknowledgement of money received against a confirmed booking
            </div>
            <div className="mt-[10px] overflow-hidden border border-neutral-300">
              <MetaRow label="Receipt No." value={receipt.number} accent />
              <MetaRow label="Receipt Date" value={formatShortDate(receipt.receiptDate)} />
              <MetaRow label="Booking No." value={receipt.bookingRef} />
            </div>
          </div>
        </div>

        {/*
          The literal header the specification asks for, kept as its own line.
          The letterhead above is brand artwork; this is the sentence that names
          the document and who issued it, which is what an auditor reads first.
        */}
        <div className="mt-4 border-y border-brand/30 py-[9px] text-center text-[12.5px] font-bold uppercase leading-none tracking-[.7px] text-brand">
          Payment Receipt — Guruji Travel Yatra
        </div>
      </header>

      {/* ── amount received ────────────────────────────────────────────── */}
      <div className="mt-5 px-7">
        <div className="rounded-[10px] border border-brand/30 bg-brand/[.05] px-5 py-4">
          <div className="flex items-start gap-5">
            <div className="min-w-0 flex-1">
              <div className="text-[9.5px] font-bold uppercase leading-none tracking-[.6px] text-brand">
                Amount Received
              </div>
              <div className="mt-[10px] font-heading text-[38px] leading-none tabular-nums">
                {formatCurrency(receipt.amount)}
              </div>
              {/*
                DERIVED from the amount, never stored. Words and digits that can
                disagree are worse on a financial document than either alone —
                the same reasoning as the credit note.
              */}
              <div
                className={cn(
                  'mt-[10px] max-h-[42px] overflow-hidden text-[9.5px] font-semibold leading-[1.45]',
                  FLUID,
                )}
              >
                {amountToWordsInr(receipt.amount)}
              </div>
            </div>

            <div className="w-[228px] flex-none text-right">
              <span
                className={cn(
                  'inline-block rounded-[20px] px-[11px] py-[6px] text-[9.5px] font-bold uppercase leading-none tracking-[.5px]',
                  settled ? 'bg-success/15 text-success-ink' : 'bg-warning/20 text-warning-ink',
                )}
              >
                {PAYMENT_STATUS_LABEL[receipt.status]}
              </span>
              <div className="mt-[9px] text-[10.5px] font-bold uppercase leading-none tracking-[.5px] text-neutral-700">
                {sequence}
              </div>
              {receipt.paymentCountTotal === null && (
                <div className="mt-[6px] text-[8.2px] leading-[1.4] text-neutral-600">
                  The instalment count is settled only once the booking is paid in full.
                </div>
              )}
            </div>
          </div>

          {/*
            An in-clearing cheque is money RECORDED, not money ARRIVED. Printing
            it as a plain receipt would hand the customer proof of a payment the
            agency has not banked.
          */}
          {caveat !== null && (
            <div className="mt-[13px] border-t border-brand/20 pt-[10px] text-[8.8px] leading-[1.45] text-warning-ink">
              <strong className="font-bold">{PAYMENT_STATUS_LABEL[receipt.status]}:</strong>{' '}
              {caveat}
            </div>
          )}
        </div>
      </div>

      {/*
        ── trip details ──────────────────────────────────────────────────
        PART C (2026-10-29). A single compact band, not its own full card —
        the sheet has no spare room for one, and this is one fact ("which
        trip, which dates"), not a block of fields. `null` (unknown tour
        or departure date) omits the whole band rather than printing a
        blank or "undefined". LONG-TEXT DISCIPLINE: capped at two lines,
        same reasoning as the customer-name block below — clipping a very
        long tour name is the lesser failure next to pushing the sheet to
        a second page.
      */}
      {receipt.tripDetails !== null && (
        <div className="mt-3 px-7">
          <div className="flex items-baseline gap-2 rounded-[8px] border border-neutral-300 bg-neutral-100 px-4 py-[7px]">
            <span className="flex-none text-[8.8px] font-bold uppercase leading-none tracking-[.5px] text-neutral-600">
              Trip
            </span>
            <span className={cn('min-w-0 max-h-[26px] flex-1 overflow-hidden text-[9.5px] font-semibold leading-[1.3]', FLUID)}>
              {receipt.tripDetails.tourName}
              {' · Departs '}
              {formatShortDate(receipt.tripDetails.departureDate)}
              {receipt.tripDetails.returnDate !== null && (
                <> {'· Returns '}{formatShortDate(receipt.tripDetails.returnDate)}</>
              )}
            </span>
          </div>
        </div>
      )}

      {/*
        ── passengers ──────────────────────────────────────────────────────
        Item 2 (2026-11-03). Same compact-band pattern as Trip above, for the
        same reason: one fact, not a card of its own, and the sheet has no
        spare room. Names only — no age, gender, ID type/number — see
        `ReceiptDto.passengerNames`'s own note on why this is the LIVE list,
        not whatever an invoice/voucher snapshotted at creation. Omitted
        entirely rather than printing "Passengers (0):" when none are
        recorded yet.
      */}
      {receipt.passengerNames.length > 0 && (
        <div className="mt-3 px-7">
          <div className="flex items-baseline gap-2 rounded-[8px] border border-neutral-300 bg-neutral-100 px-4 py-[7px]">
            <span className="flex-none text-[8.8px] font-bold uppercase leading-none tracking-[.5px] text-neutral-600">
              Passengers ({receipt.passengerNames.length})
            </span>
            <span className={cn('min-w-0 max-h-[38px] flex-1 overflow-hidden text-[9.5px] font-semibold leading-[1.3]', FLUID)}>
              {passengerSummary}
            </span>
          </div>
        </div>
      )}

      {/* ── received from · payment details ────────────────────────────── */}
      <div className="mt-6 grid grid-cols-2 gap-3 px-7">
        <div className="relative min-h-[152px] rounded-[10px] border border-brand/30 px-4 pb-3 pt-[18px]">
          <SectionTab label="Received From" icon={PERSON_ICON} />
          <div className="flex flex-col gap-[9px]">
            {/*
              The name gets its own block rather than a one-line Field: it is the
              longest free-text value on the sheet, and a 200-character name has
              to wrap somewhere that does not shove the cards below it off the
              page. Three lines, then clipped.
            */}
            <div>
              <div className="text-[9.5px] leading-[1.5]">Customer</div>
              <div
                className={cn(
                  /**
                   * FIVE LINES (90px at 12px/1.5), not three.
                   *
                   * Three fitted the artwork but clipped a real 154-character
                   * Indian name mid-word, and a receipt that truncates the payer
                   * is a receipt they can dispute. Five covers ~250 characters
                   * and still leaves the sheet ~60px of slack above the footer,
                   * so the bound survives — it is just set where clipping is
                   * genuinely the lesser evil rather than merely convenient.
                   */
                  'mt-[3px] max-h-[90px] overflow-hidden border-b border-dotted border-neutral-400 pb-[3px] text-[12px] font-bold leading-[1.5]',
                  FLUID,
                )}
              >
                {receipt.customerName}
              </div>
            </div>
            <Field label="Booking Reference" value={receipt.bookingRef} />
            <Field label="Receipt Number" value={receipt.number} />
            <Field label="Receipt Date" value={formatShortDate(receipt.receiptDate)} />
          </div>
        </div>

        <div className="relative min-h-[152px] rounded-[10px] border border-brand/30 px-4 pb-3 pt-[18px]">
          <SectionTab label="Payment Details" icon={DOC_ICON} />
          <div className="flex flex-col gap-[9px]">
            <Field label="Payment Date" value={formatShortDate(receipt.paidAt)} />
            <Field label="Payment Method" value={receipt.mode} />
            <Field
              label="Transaction Ref."
              /* Empty for cash, which has no instrument to quote. An em dash is
                 honest; inventing a reference would not be. */
              value={
                receipt.transactionReference.length > 0 ? receipt.transactionReference : '—'
              }
            />
            <Field label="Instalment" value={sequence} />
          </div>
        </div>
      </div>

      {/* ── balance ────────────────────────────────────────────────────── */}
      <div className="mt-6 px-7">
        <div className="overflow-hidden rounded-[10px] border border-neutral-300">
          <div className="bg-brand px-4 py-[8px] text-[9.5px] font-bold uppercase leading-none tracking-[.6px] text-white">
            Account Position
          </div>

          {/*
            No explanatory sentence below the tiles any more, and no "as at
            this receipt" qualifier on the heading above — deliberately, per
            the 2026-10-23 revision-visibility change. `bookingTotal` is now
            live, so a sentence here would either repeat the three labelled
            figures for no reason or risk one of the words this document must
            never print ("revised"/"previous"/"earlier"/"was") or the old
            total creeping into free text. The three labelled tiles are the
            whole of what this block says.
          */}
          <div className="grid grid-cols-3 divide-x divide-neutral-200">
            {(
              [
                ['Booking total', receipt.bookingTotal, 'text-ink'],
                ['Paid up to this receipt', receipt.paidToDate, 'text-success-ink'],
                ['Balance due', receipt.remaining, receipt.remaining > 0 ? 'text-brand' : 'text-success-ink'],
              ] as ReadonlyArray<readonly [string, number, string]>
            ).map(([label, value, tone]) => (
              <div key={label} className="px-4 py-[13px]">
                <div className="text-[8.8px] font-semibold uppercase leading-none tracking-[.5px] text-neutral-600">
                  {label}
                </div>
                <div className={cn('mt-[8px] font-heading text-[19px] leading-none tabular-nums', tone)}>
                  {formatCurrency(value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── notes · signature ──────────────────────────────────────────── */}
      <div className="mt-6 grid grid-cols-[1fr_260px] gap-3 px-7">
        <div className="rounded-[10px] border border-neutral-300 px-4 py-[13px]">
          <div className="text-[8.8px] font-bold uppercase leading-none tracking-[.5px] text-neutral-600">
            Please Note
          </div>
          <ul className="mt-[9px] flex flex-col gap-[6px]">
            {[
              'This receipt acknowledges the amount shown above and is valid without a physical signature.',
              'A receipt cannot be amended. A correction is recorded as a separate compensating entry, so both remain on the booking ledger.',
              'Cheque payments are credited only once the funds clear the bank.',
              'Please quote the receipt number in any correspondence about this payment.',
            ].map((line) => (
              <li key={line} className="flex gap-[7px] text-[8.6px] leading-[1.45]">
                <span className="mt-[5px] h-[3px] w-[3px] flex-none rounded-full bg-brand" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-[10px] border border-brand/30 px-4 py-[13px] text-center">
          <div className="text-[8.8px] font-bold uppercase leading-none tracking-[.5px] text-neutral-600">
            For {COMPANY.displayName}
          </div>
          <div className="mt-[34px] border-b border-neutral-400" />
          <div className="mt-[7px] text-[8.6px] uppercase tracking-[.4px] text-neutral-600">
            Authorised Signatory
          </div>
        </div>
      </div>

      {/* ── footer ─────────────────────────────────────────────────────── */}
      <footer className="mt-auto flex items-stretch bg-gradient-to-r from-brand to-brand-dark px-7 py-[13px] text-white">
        <div className="flex flex-none items-center gap-[10px] pr-5">
          <img src="/brand/guruji-mark-white.png" alt="" className="h-auto w-[36px] flex-none" />
          <div>
            <div className="text-[13.5px] font-bold uppercase leading-none tracking-[.4px]">
              {COMPANY.displayName}
            </div>
            <div className="mt-[5px] text-[9px] leading-none opacity-90">
              {COMPANY.tagline} {COMPANY.taglineAccent}
            </div>
          </div>
        </div>

        <div className="flex flex-1 items-center gap-5 border-l border-white/25 pl-5 text-[9.4px] leading-[1.5]">
          <div className="flex flex-col gap-[4px]">
            <span>{COMPANY.phone}</span>
            <span>{COMPANY.email}</span>
            <span>{COMPANY.website}</span>
          </div>
          <div className="ml-auto text-right">
            {COMPANY.address.lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
            <div>{formatCompanyLocality()}</div>
            <div className="mt-[5px] inline-flex items-stretch overflow-hidden rounded-[3px]">
              <span className="bg-ink px-[6px] py-[3px] text-[8.6px] font-bold tracking-[.6px]">
                GSTIN
              </span>
              <span className="bg-white px-[6px] py-[3px] text-[8.6px] font-bold tracking-[.4px] text-ink">
                {COMPANY.gstin}
              </span>
            </div>
          </div>
        </div>
      </footer>
    </article>
  );
}
